import { Box, useTheme } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface OnboardingHighlightProps {
  targetId: string;
  padding?: number;
  paddingX?: number;
  paddingY?: number;
  paddingTop?: number;
  paddingBottom?: number;
  borderRadius?: number;
  shape?: 'rectangle' | 'tShape';
  buttonWidth?: number; // Width of the top part for T-shape
  buttonHeight?: number; // Height of the top part for T-shape
  onRectChange?: (rect: HighlightRect | null) => void;
}

const OnboardingHighlight = ({
  targetId,
  padding = 8,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  borderRadius = 12,
  shape = 'rectangle',
  buttonWidth = 70,
  buttonHeight = 30,
  onRectChange,
}: OnboardingHighlightProps) => {
  const theme = useTheme();
  const [rect, setRect] = useState<HighlightRect | null>(null);

  const px = paddingX ?? padding;
  const py = paddingY ?? padding;
  const pt = paddingTop ?? py;
  const pb = paddingBottom ?? py;

  const updateRect = useCallback(() => {
    // Use ~= selector to match space-separated words in data-onboarding-id
    // Find ALL matching elements and combine their bounding boxes
    const elements = document.querySelectorAll(`[data-onboarding-id~="${targetId}"]`);
    if (elements.length > 0) {
      // Calculate combined bounding box for all matching elements
      let minTop = Infinity;
      let minLeft = Infinity;
      let maxRight = -Infinity;
      let maxBottom = -Infinity;

      elements.forEach((element) => {
        const boundingRect = element.getBoundingClientRect();
        minTop = Math.min(minTop, boundingRect.top);
        minLeft = Math.min(minLeft, boundingRect.left);
        maxRight = Math.max(maxRight, boundingRect.right);
        maxBottom = Math.max(maxBottom, boundingRect.bottom);
      });

      const newRect = {
        top: minTop - pt,
        left: minLeft - px,
        width: (maxRight - minLeft) + px * 2,
        height: (maxBottom - minTop) + pt + pb,
      };
      setRect(newRect);
      onRectChange?.(newRect);
    } else {
      setRect(null);
      onRectChange?.(null);
    }
  }, [targetId, px, pt, pb, onRectChange]);

  useEffect(() => {
    // Initial update with a small delay to ensure element is rendered
    const timeoutId = setTimeout(updateRect, 100);

    // Update on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(updateRect);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Observe DOM changes in case the element appears later
    // Only observe direct children to avoid excessive firing on every DOM mutation
    const observer = new MutationObserver(handleUpdate);
    observer.observe(document.body, { childList: true, subtree: false });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      observer.disconnect();
    };
  }, [updateRect]);

  if (!rect) return null;

  const viewportWidth = window.innerWidth;

  // For T-shape, we need separate circle cutout
  const getCircleCutout = () => {
    if (shape !== 'tShape') return null;
    const buttonCenterX = viewportWidth / 2;
    const buttonRadius = buttonWidth / 2;
    const circleCenterY = rect.top + buttonRadius;
    return { cx: buttonCenterX, cy: circleCenterY, r: buttonRadius };
  };

  const circleCutout = getCircleCutout();

  return (
    <>
      {/* Full screen dark overlay with cutout */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: theme.zIndex.modal + 5,
          pointerEvents: 'none',
        }}
      >
        <svg
          width="100%"
          height="100%"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          <defs>
            <mask id="highlight-mask">
              <rect width="100%" height="100%" fill="white" />
              {shape === 'tShape' ? (
                <>
                  {/* Circle cutout for button */}
                  {circleCutout && (
                    <circle
                      cx={circleCutout.cx}
                      cy={circleCutout.cy}
                      r={circleCutout.r}
                      fill="black"
                    />
                  )}
                  {/* Rectangle cutout for bar */}
                  <rect
                    x={rect.left}
                    y={rect.top + buttonHeight}
                    width={rect.width}
                    height={rect.height - buttonHeight}
                    fill="black"
                  />
                </>
              ) : (
                /* Standard rectangle cutout with rounded corners */
                <rect
                  x={rect.left}
                  y={rect.top}
                  width={rect.width}
                  height={rect.height}
                  rx={borderRadius}
                  ry={borderRadius}
                  fill="black"
                  style={{
                    transition: 'x 0.3s ease, y 0.3s ease, width 0.3s ease, height 0.3s ease',
                  }}
                />
              )}
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.6)"
            mask="url(#highlight-mask)"
          />
        </svg>
      </Box>

    </>
  );
};

export default OnboardingHighlight;
